import React, { useMemo, useState } from 'react';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import KpiChartPanel from './charts/KpiChartPanel';
import {
  KPI_DASHBOARD_TABLE_TH,
  KPI_DASHBOARD_TABLE_TH_CENTER,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';

export interface KpiRequestsByCustomerTableProps {
  title?: string;
  /** Column header for the numeric count (e.g. `Requests`, `Cases`). */
  countColumnLabel?: string;
  rows: Array<{ customerId: string; customerName: string; count: number }>;
  onRowClick?: (customerId: string) => void;
  emptyMessage?: string;
}

type SortKey = 'name' | 'count';

const KpiRequestsByCustomerTable: React.FC<KpiRequestsByCustomerTableProps> = ({
  title = 'Requests by Customer',
  countColumnLabel = 'Requests',
  rows,
  onRowClick,
  emptyMessage = 'No service requests for the selected filters.',
}) => {
  const [sort, setSort] = useState<{ by: SortKey; desc: boolean }>({ by: 'count', desc: true });

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      if (sort.by === 'name') {
        const cmp = a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' });
        return sort.desc ? -cmp : cmp;
      }
      const cmp = a.count - b.count;
      return sort.desc ? -cmp : cmp;
    });
    return copy;
  }, [rows, sort]);

  const toggle = (by: SortKey) => {
    setSort((prev) =>
      prev.by === by ? { by, desc: !prev.desc } : { by, desc: by === 'count' }
    );
  };

  const sortBtn =
    'inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left font-semibold text-slate-800 hover:bg-sky-200/50';

  return (
    <KpiChartPanel
      title={title}
      flexFill
      className="min-h-0 lg:max-h-[500px]"
      bodyClassName="flex min-h-0 flex-1 flex-col px-0 pb-0 pt-0"
    >
      <div className="max-h-[min(28rem,70vh)] min-h-0 flex-1 overflow-auto">
        <table className="w-max min-w-full border-collapse text-left text-xs">
          <thead>
            <tr>
              <th className={KPI_DASHBOARD_TABLE_TH} scope="col">
                <button type="button" onClick={() => toggle('name')} className={sortBtn}>
                  Customer
                  <ArrowsUpDownIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden />
                </button>
              </th>
              <th className={`${KPI_DASHBOARD_TABLE_TH_CENTER} w-28`} scope="col">
                <button type="button" onClick={() => toggle('count')} className={`${sortBtn} justify-center`}>
                  {countColumnLabel}
                  <ArrowsUpDownIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={2}
                  className="border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sorted.map((row, ri) => {
                const clickable = Boolean(onRowClick && row.count > 0 && row.customerId);
                return (
                  <tr key={row.customerId} className={`border-b border-slate-200 ${kpiDashboardTableRowClass(ri)}`}>
                    <td
                      className={`max-w-[min(28rem,55vw)] border border-slate-200 px-3 py-2 font-medium text-slate-900 ${
                        clickable ? 'cursor-pointer hover:bg-sky-50/80' : ''
                      }`}
                      onClick={clickable ? () => onRowClick?.(row.customerId) : undefined}
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onRowClick?.(row.customerId);
                              }
                            }
                          : undefined
                      }
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                    >
                      <span className="line-clamp-2">{row.customerName}</span>
                    </td>
                    <td
                      className={`border border-slate-200 px-2 py-2 text-center tabular-nums text-slate-800 ${
                        clickable ? 'cursor-pointer hover:bg-sky-50/80' : ''
                      }`}
                      onClick={clickable ? () => onRowClick?.(row.customerId) : undefined}
                    >
                      {row.count.toLocaleString()}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </KpiChartPanel>
  );
};

export default KpiRequestsByCustomerTable;
